import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Users, History } from "lucide-react";

/**
 * Client autocomplete input — fetches past clients of the current
 * partner/sales/admin user and suggests them as the user types.
 *
 * When a suggestion is picked, `onPick(client)` receives
 *   { name, email, phone, usage_count, last_seen }
 * so the parent can prefill all 3 fields (name + email + phone) at once.
 */
export default function ClientAutocomplete({
  value,
  onChange,
  onPick,
  field, // "name" | "email" | "phone" — which field this input maps to
  placeholder,
  testid,
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [allClients, setAllClients] = useState(null);
  const blurTimer = useRef(null);

  // Lazy-load all past clients on first focus (single small fetch).
  const loadOnce = async () => {
    if (allClients !== null) return;
    try {
      const { data } = await api.get("/partner/clients", { params: { limit: 100 } });
      setAllClients(data.items || []);
    } catch {
      setAllClients([]);
    }
  };

  useEffect(() => {
    if (!allClients) {
      setItems([]);
      return;
    }
    const q = (value || "").trim().toLowerCase();
    if (!q) {
      setItems(allClients.slice(0, 6));
      return;
    }
    const filtered = allClients
      .filter(
        (c) =>
          (c[field] || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.name || "").toLowerCase().includes(q) ||
          (c.phone || "").toLowerCase().includes(q),
      )
      .slice(0, 8);
    setItems(filtered);
  }, [value, allClients, field]);

  const handlePick = (c) => {
    onPick && onPick(c);
    setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          loadOnce();
          setOpen(true);
          if (blurTimer.current) clearTimeout(blurTimer.current);
        }}
        onBlur={() => {
          // Delay close so onClick on suggestion fires first
          blurTimer.current = setTimeout(() => setOpen(false), 180);
        }}
        placeholder={placeholder}
        className="bg-zinc-950/50 border-white/10 mt-2"
        data-testid={testid}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-orange-500/20 bg-zinc-950 shadow-xl"
          data-testid={`${testid}-suggestions`}
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5 flex items-center gap-1.5">
            <History size={11} />
            Clienți anteriori ({items.length})
          </div>
          {items.map((c) => (
            <button
              type="button"
              key={c.email}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handlePick(c)}
              className="w-full text-left px-3 py-2 hover:bg-orange-500/10 border-b border-white/5 last:border-b-0 transition-colors"
              data-testid={`${testid}-suggestion-${c.email}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-white text-sm truncate">
                  {c.name || c.email}
                </div>
                {c.usage_count > 1 && (
                  <div className="text-[10px] text-orange-400 font-bold shrink-0">
                    × {c.usage_count}
                  </div>
                )}
              </div>
              <div className="text-[11px] text-zinc-500 truncate">
                {c.email}
                {c.phone ? ` · ${c.phone}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && allClients && allClients.length === 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 rounded-md border border-white/10 bg-zinc-950 px-3 py-3 text-xs text-zinc-500 flex items-center gap-2"
          data-testid={`${testid}-empty`}
        >
          <Users size={12} />
          Niciun client anterior — primul tău client va apărea aici după prima
          simulare cu date complete.
        </div>
      )}
    </div>
  );
}
